import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { FormsModule, Validators } from '@angular/forms';
import { Params } from '@angular/router';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
  MatExpansionPanelDescription,
} from '@angular/material/expansion';
import { Subject, takeUntil } from 'rxjs';
import { ErrorObject } from 'src/app/_models/error';
import { FormBase } from 'src/app/_models/form-base';
import { CreateExperiment } from 'src/app/_models/experiment-create';
import { ExperimentUpdate } from 'src/app/_models/experiment-update';
import { ExperimentService } from 'src/app/_services/experiment.service';
import { Experiment } from 'src/app/_models/experiment';
import { ExperimentStatus } from 'src/app/_models/experiment-status';
import { ChangelogResponse } from 'src/app/_models/api-responses';
import {
  ExperimentMode,
  ExperimentModeDisplay,
} from 'src/app/_models/experiment-mode';
import {
  allDevicesHaveRequiredModules,
  advancedNameRegex,
} from 'src/app/_helpers/utils';
import { AuthenticationService } from 'src/app/_services/authentication.service';
import { stepValidator } from 'src/app/_helpers/step-validator.validator';
import { User } from 'src/app/_models/user';
import { RoleCheckPipe } from '../../_helpers/role-check.pipe';
import { ValidationModalComponent } from '../../_components/modals/validation-modal/validation-modal.component';
import { ModalComponent } from '../../_components/modals/modal.component';
import { ErrorPageComponent } from '../../_components/error-page/error-page.component';
import { IconButtonComponent } from '../../_components/buttons/icon-button/icon-button.component';
import { DropdownFormFieldComponent } from '../../_components/forms/dropdown-form-field/dropdown-form-field.component';
import { MatIcon } from '@angular/material/icon';
import { UnderlinedTextareaFieldComponent } from '../../_components/forms/underlined-textarea-field/underlined-textarea-field.component';
import { SlideToggleComponent } from '../../_components/selection-controls/slide-toggle/slide-toggle.component';
import { UnderlinedFormFieldComponent } from '../../_components/forms/underlined-form-field/underlined-form-field.component';
import { ButtonComponent } from '../../_components/buttons/button/button.component';
import { RadioButtonComponent } from '../../_components/selection-controls/radio-button/radio-button.component';
import { StatusIndicatorComponent } from '../../_components/chips/status-indicator/status-indicator.component';
import { DatePipe } from '@angular/common';
import { Flask } from 'src/app/_helpers/experiment-utils';
import { NumberFormFieldComponent } from 'src/app/_components/forms/number-form-field/number-form-field.component';
import { ExperimentParameterRanges } from 'src/app/_helpers/experiment-min-max';
import { NotesParameterRanges } from 'src/app/_helpers/notes-min-max';
import { FluorescenceChannel } from 'src/app/_models/fluorescence-channel';
import { UnderlinedDropdownFormFieldComponent } from 'src/app/_components/forms/underlined-dropdown-form-field/underlined-dropdown-form-field.component';
import { SmallLoadingSpinnerComponent } from 'src/app/_components/small-loading-spinner/small-loading-spinner.component';
import { FluorescenceChannelCardComponent } from 'src/app/_components/cards/fluorescence-channel-card/fluorescence-channel-card.component';
import { ExperimentTemplateService } from 'src/app/_services/experiment-template.service';
import {
  ExperimentTemplate,
  ExperimentTemplateNames,
} from 'src/app/_models/experiment-template';
import { MatOutlinedButtonComponent } from 'src/app/_components/buttons/mat-outlined-button/mat-outlined-button.component';
import { UnderlinedTextButtonComponent } from 'src/app/_components/buttons/underlined-text-button/underlined-text-button.component';
import { ChipRadioButtonComponent } from 'src/app/_components/selection-controls/chip-radio-button/chip-radio-button.component';
import { ChipCheckboxGroupComponent } from 'src/app/_components/selection-controls/chip-checkbox-group/chip-checkbox-group.component';
import { MatIconButtonComponent } from 'src/app/_components/buttons/mat-icon-button/mat-icon-button.component';
import { MatTableModule } from '@angular/material/table';
import { ExperimentFormBase } from '../_base/experiment-form.base';
import { FormatRolePipe } from 'src/app/_helpers/format-role.pipe';

@Component({
  selector: 'app-set-parameters',
  templateUrl: './set-parameters.component.html',
  styleUrls: ['./set-parameters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    StatusIndicatorComponent,
    RadioButtonComponent,
    ButtonComponent,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    FormsModule,
    UnderlinedFormFieldComponent,
    SlideToggleComponent,
    MatExpansionPanelDescription,
    UnderlinedTextareaFieldComponent,
    MatIcon,
    DropdownFormFieldComponent,
    IconButtonComponent,
    ErrorPageComponent,
    ModalComponent,
    ValidationModalComponent,
    DatePipe,
    RoleCheckPipe,
    NumberFormFieldComponent,
    UnderlinedDropdownFormFieldComponent,
    FluorescenceChannelCardComponent,
    MatOutlinedButtonComponent,
    UnderlinedTextButtonComponent,
    ChipRadioButtonComponent,
    ChipCheckboxGroupComponent,
    MatIconButtonComponent,
    MatTableModule,
    FormatRolePipe,
    SmallLoadingSpinnerComponent,
  ],
})
export class SetParametersComponent
  extends ExperimentFormBase
  implements OnInit, OnDestroy
{
  experimentId = signal<number | null>(null);
  override readonly entityId = this.experimentId;
  override fetchChangelog(): void {
    this.getExperimentChangelog();
  }
  override getSavedEntity(): Experiment | null {
    return this.createdExperiment();
  }

  protected override onAfterExperimentModeChange(): void {
    if (this.createdExperiment()?.devices?.length > 0) {
      this.checkRequiredModules();
    }
  }

  protected override onAfterModuleCheckboxClicked(): void {
    if (this.createdExperiment()?.devices?.length > 0) {
      this.checkRequiredModules();
    }
  }

  private readonly authenticationService = inject(AuthenticationService);
  protected experimentService = inject(ExperimentService);
  protected experimentTemplateService = inject(ExperimentTemplateService);

  // Control vars
  error = signal<ErrorObject | null>(null);
  fetchTemplatesError = signal<ErrorObject | null>(null);
  fetchTemplatesLoading = signal<boolean>(false);
  changelogError = signal<ErrorObject | null>(null);
  getExperimentError = signal<ErrorObject | null>(null);
  deleteError = signal<ErrorObject | null>(null);
  // Possibly delete isLoading - unless we want to hide form if theres an error getting experiment?
  loading = signal<boolean>(false);
  deleteIsProcessing = signal<boolean>(false);
  nextButtonDisabled = signal<boolean>(true);
  draftIsLocked = signal<boolean>(false);

  // Notes
  notesMinLength = NotesParameterRanges.EXPERIMENT_NOTES.minNoteLength;
  notesMaxLength = NotesParameterRanges.EXPERIMENT_NOTES.maxNoteLength;

  // Make experiment status enum available in template
  ExperimentStatus = ExperimentStatus;
  // Make enums available in template
  ExperimentParameterRanges = ExperimentParameterRanges;
  NoteParameterRanges = NotesParameterRanges;
  ExperimentModeDisplay = ExperimentModeDisplay;

  private readonly cancelTemplates$ = new Subject<void>();
  private subscriptionsInitialised = false;

  // Template vars
  templateId = signal<number | null>(null);
  experimentTemplates = signal<ExperimentTemplateNames[]>([]);

  newExperiment = signal<CreateExperiment | ExperimentTemplate | null>(null);
  // Created experiment
  createdExperiment = signal<Experiment | null>(null);
  unsavedCachedData = signal<Experiment | CreateExperiment | null>(null);

  // Table vars
  currentFilters = [];

  noteField = new FormBase({
    key: 'noteField',
    label: 'Note',
    type: 'textarea',
    placeholder: 'Enter notes here...',
    disabled: false,
    required: false,
    value: '',
    options: [],
  });

  experimentTemplatesField = new FormBase({
    key: 'experimentTemplates',
    label: 'Select Template (optional)',
    type: 'dropdown',
    placeholder: 'Select template...',
    disabled: false,
    required: false,
    value: null,
    options: [],
  });

  override experimentControlsForm = this.formBuilder.group({
    // General experiment controls
    [this.experimentNameField.key]: [
      '',
      [
        Validators.minLength(2),
        Validators.maxLength(100),
        Validators.required,
        Validators.pattern(advancedNameRegex),
      ],
    ],
    [this.organismField.key]: [
      '',
      [Validators.minLength(2), Validators.maxLength(100), Validators.required],
    ],
    [this.strainField.key]: [
      '',
      [Validators.minLength(2), Validators.maxLength(100), Validators.required],
    ],
    [this.mediaField.key]: [
      '',
      [Validators.minLength(2), Validators.maxLength(100), Validators.required],
    ],
    [this.samplingTimeField.key]: [
      this.samplingTimeField.value,
      Validators.compose([
        Validators.min(this.minSampleTimeInMins),
        Validators.max(this.maxSampleTimeInMins),
        Validators.required,
        stepValidator(this.sampleTimeStep),
      ]),
    ],
    [this.experimentTimeField.key]: [
      this.experimentTimeField.value,
      Validators.compose([
        Validators.min(this.minExperimentTimeInHours),
        Validators.max(this.maxExperimentTimeInHours),
        Validators.required,
        stepValidator(this.experimentTimeStep),
      ]),
    ],
    [this.speedAField.key]: [
      this.speedAField.value,
      Validators.compose([
        Validators.min(this.minSpeed),
        Validators.max(this.maxSpeed),
        stepValidator(this.speedStep),
      ]),
    ],
    [this.speedBField.key]: [
      this.speedBField.value,
      Validators.compose([
        Validators.min(this.minSpeed),
        Validators.max(this.maxSpeed),
        stepValidator(this.speedStep),
      ]),
    ],
    [this.speedCField.key]: [
      this.speedCField.value,
      Validators.compose([
        Validators.min(this.minSpeed),
        Validators.max(this.maxSpeed),
        stepValidator(this.speedStep),
      ]),
    ],
    [this.speedDField.key]: [
      this.speedDField.value,
      Validators.compose([
        Validators.min(this.minSpeed),
        Validators.max(this.maxSpeed),
        stepValidator(this.speedStep),
      ]),
    ],
    [this.setStirringSpeedAField.key]: [this.setStirringSpeedAField.value],
    [this.setStirringSpeedBField.key]: [this.setStirringSpeedBField.value],
    [this.setStirringSpeedCField.key]: [this.setStirringSpeedCField.value],
    [this.setStirringSpeedDField.key]: [this.setStirringSpeedDField.value],
    [this.ambientTemperatureField.key]: [
      this.ambientTemperatureField.value,
      [
        Validators.min(this.minAmbientTemp),
        Validators.max(this.maxAmbientTemp),
        stepValidator(this.ambientTempStep),
      ],
    ],
    [this.temperatureControlAField.key]: [this.temperatureControlAField.value],
    [this.temperatureControlBField.key]: [this.temperatureControlBField.value],
    [this.temperatureControlCField.key]: [this.temperatureControlCField.value],
    [this.temperatureControlDField.key]: [this.temperatureControlDField.value],
    [this.temperatureAField.key]: [
      this.temperatureAField.value,
      [
        Validators.min(this.minCultureTemp),
        Validators.max(this.maxAmbientTemp),
        stepValidator(this.cultureTempStep),
      ],
    ],
    [this.temperatureBField.key]: [
      this.temperatureBField.value,
      [
        Validators.min(this.minCultureTemp),
        Validators.max(this.maxAmbientTemp),
        stepValidator(this.cultureTempStep),
      ],
    ],
    [this.temperatureCField.key]: [
      this.temperatureCField.value,
      [
        Validators.min(this.minCultureTemp),
        Validators.max(this.maxAmbientTemp),
        stepValidator(this.cultureTempStep),
      ],
    ],
    [this.temperatureDField.key]: [
      this.temperatureDField.value,
      [
        Validators.min(this.minCultureTemp),
        Validators.max(this.maxAmbientTemp),
        stepValidator(this.cultureTempStep),
      ],
    ],
    [this.maintainODField.key]: [this.maintainODField.value],
    // Turbidostat controls
    [this.targetODAField.key]: [
      this.targetODAField.value,
      [
        Validators.min(this.minTargetOD),
        Validators.max(this.maxTargetOD),
        stepValidator(this.targetODStep),
      ],
    ],
    [this.targetODBField.key]: [
      this.targetODBField.value,
      [
        Validators.min(this.minTargetOD),
        Validators.max(this.maxTargetOD),
        stepValidator(this.targetODStep),
      ],
    ],
    [this.targetODCField.key]: [
      this.targetODCField.value,
      [
        Validators.min(this.minTargetOD),
        Validators.max(this.maxTargetOD),
        stepValidator(this.targetODStep),
      ],
    ],
    [this.targetODDField.key]: [
      this.targetODDField.value,
      [
        Validators.min(this.minTargetOD),
        Validators.max(this.maxTargetOD),
        stepValidator(this.targetODStep),
      ],
    ],
    [this.turbAActiveField.key]: [this.turbAActiveField.value],
    [this.turbBActiveField.key]: [this.turbBActiveField.value],
    [this.turbCActiveField.key]: [this.turbCActiveField.value],
    [this.turbDActiveField.key]: [this.turbDActiveField.value],
    // Chemostat controls
    [this.chemoAActiveField.key]: [this.chemoAActiveField.value],
    [this.chemoBActiveField.key]: [this.chemoBActiveField.value],
    [this.chemoCActiveField.key]: [this.chemoCActiveField.value],
    [this.chemoDActiveField.key]: [this.chemoDActiveField.value],
    [this.flowRateAField.key]: [
      this.flowRateAField.value,
      Validators.compose([
        Validators.min(this.minFlowRate),
        Validators.max(this.maxFlowRate),
        stepValidator(this.flowRateStep),
      ]),
    ],
    [this.flowRateBField.key]: [
      this.flowRateBField.value,
      Validators.compose([
        Validators.min(this.minFlowRate),
        Validators.max(this.maxFlowRate),
        stepValidator(this.flowRateStep),
      ]),
    ],
    [this.flowRateCField.key]: [
      this.flowRateCField.value,
      Validators.compose([
        Validators.min(this.minFlowRate),
        Validators.max(this.maxFlowRate),
        stepValidator(this.flowRateStep),
      ]),
    ],
    [this.flowRateDField.key]: [
      this.flowRateDField.value,
      Validators.compose([
        Validators.min(this.minFlowRate),
        Validators.max(this.maxFlowRate),
        stepValidator(this.flowRateStep),
      ]),
    ],
    // Morbidostat controls
    [this.morbidostatAActiveField.key]: [this.morbidostatAActiveField.value],
    [this.morbidostatBActiveField.key]: [this.morbidostatBActiveField.value],
    [this.morbidostatCActiveField.key]: [this.morbidostatCActiveField.value],
    [this.morbidostatDActiveField.key]: [this.morbidostatDActiveField.value],
    [this.thresholdODAField.key]: [
      this.thresholdODAField.value,
      Validators.compose([
        Validators.min(this.minThreshold),
        Validators.max(this.maxThreshold),
        stepValidator(this.thresholdStep),
      ]),
    ],
    [this.thresholdODBField.key]: [
      this.thresholdODBField.value,
      Validators.compose([
        Validators.min(this.minThreshold),
        Validators.max(this.maxThreshold),
        stepValidator(this.thresholdStep),
      ]),
    ],
    [this.thresholdODCField.key]: [
      this.thresholdODCField.value,
      Validators.compose([
        Validators.min(this.minThreshold),
        Validators.max(this.maxThreshold),
        stepValidator(this.thresholdStep),
      ]),
    ],
    [this.thresholdODDField.key]: [
      this.thresholdODDField.value,
      Validators.compose([
        Validators.min(this.minThreshold),
        Validators.max(this.maxThreshold),
        stepValidator(this.thresholdStep),
      ]),
    ],
    [this.dilutionPeriodAField.key]: [
      this.dilutionPeriodAField.value,
      Validators.compose([
        Validators.min(this.minDilutionPeriod),
        Validators.max(this.maxDilutionPeriod),
        stepValidator(this.dilutionPeriodStep),
      ]),
    ],
    [this.dilutionPeriodBField.key]: [
      this.dilutionPeriodBField.value,
      Validators.compose([
        Validators.min(this.minDilutionPeriod),
        Validators.max(this.maxDilutionPeriod),
        stepValidator(this.dilutionPeriodStep),
      ]),
    ],
    [this.dilutionPeriodCField.key]: [
      this.dilutionPeriodCField.value,
      Validators.compose([
        Validators.min(this.minDilutionPeriod),
        Validators.max(this.maxDilutionPeriod),
        stepValidator(this.dilutionPeriodStep),
      ]),
    ],
    [this.dilutionPeriodDField.key]: [
      this.dilutionPeriodDField.value,
      Validators.compose([
        Validators.min(this.minDilutionPeriod),
        Validators.max(this.maxDilutionPeriod),
        stepValidator(this.dilutionPeriodStep),
      ]),
    ],
    [this.dilutionFactorAField.key]: [
      this.dilutionFactorAField.value,
      Validators.compose([
        Validators.min(this.minDilutionFactor),
        Validators.max(this.maxDilutionFactor),
        stepValidator(this.dilutionFactorStep),
      ]),
    ],
    [this.dilutionFactorBField.key]: [
      this.dilutionFactorBField.value,
      Validators.compose([
        Validators.min(this.minDilutionFactor),
        Validators.max(this.maxDilutionFactor),
        stepValidator(this.dilutionFactorStep),
      ]),
    ],
    [this.dilutionFactorCField.key]: [
      this.dilutionFactorCField.value,
      Validators.compose([
        Validators.min(this.minDilutionFactor),
        Validators.max(this.maxDilutionFactor),
        stepValidator(this.dilutionFactorStep),
      ]),
    ],
    [this.dilutionFactorDField.key]: [
      this.dilutionFactorDField.value,
      Validators.compose([
        Validators.min(this.minDilutionFactor),
        Validators.max(this.maxDilutionFactor),
        stepValidator(this.dilutionFactorStep),
      ]),
    ],
    // pH controls
    [this.phControlAField.key]: [this.phControlAField.value],
    [this.phControlBField.key]: [this.phControlBField.value],
    [this.phControlCField.key]: [this.phControlCField.value],
    [this.phControlDField.key]: [this.phControlDField.value],
    [this.targetPHAField.key]: [
      this.targetPHAField.value,
      Validators.compose([
        Validators.min(this.minTargetPH),
        Validators.max(this.maxTargetPH),
        stepValidator(this.targetPHStep),
      ]),
    ],
    [this.targetPHBField.key]: [
      this.targetPHBField.value,
      Validators.compose([
        Validators.min(this.minTargetPH),
        Validators.max(this.maxTargetPH),
        stepValidator(this.targetPHStep),
      ]),
    ],
    [this.targetPHCField.key]: [
      this.targetPHCField.value,
      Validators.compose([
        Validators.min(this.minTargetPH),
        Validators.max(this.maxTargetPH),
        stepValidator(this.targetPHStep),
      ]),
    ],
    [this.targetPHDField.key]: [
      this.targetPHDField.value,
      Validators.compose([
        Validators.min(this.minTargetPH),
        Validators.max(this.maxTargetPH),
        stepValidator(this.targetPHStep),
      ]),
    ],
    [this.inputTypeAField.key]: [this.inputTypeAField.value],
    [this.inputTypeBField.key]: [this.inputTypeBField.value],
    [this.inputTypeCField.key]: [this.inputTypeCField.value],
    [this.inputTypeDField.key]: [this.inputTypeDField.value],
    [this.pumpDurationAField.key]: [
      this.pumpDurationAField.value,
      Validators.compose([
        Validators.min(this.minPumpDuration),
        Validators.max(this.maxPumpDuration),
        stepValidator(this.pumpDurationStep),
      ]),
    ],
    [this.pumpDurationBField.key]: [
      this.pumpDurationBField.value,
      Validators.compose([
        Validators.min(this.minPumpDuration),
        Validators.max(this.maxPumpDuration),
        stepValidator(this.pumpDurationStep),
      ]),
    ],
    [this.pumpDurationCField.key]: [
      this.pumpDurationCField.value,
      Validators.compose([
        Validators.min(this.minPumpDuration),
        Validators.max(this.maxPumpDuration),
        stepValidator(this.pumpDurationStep),
      ]),
    ],
    [this.pumpDurationDField.key]: [
      this.pumpDurationDField.value,
      Validators.compose([
        Validators.min(this.minPumpDuration),
        Validators.max(this.maxPumpDuration),
        stepValidator(this.pumpDurationStep),
      ]),
    ],
    // pH 2x controls
    [this.ph2ControlAField.key]: [this.ph2ControlAField.value],
    [this.ph2ControlBField.key]: [this.ph2ControlBField.value],
    [this.targetPH2AField.key]: [
      this.targetPH2AField.value,
      Validators.compose([
        Validators.min(this.minTargetPH),
        Validators.max(this.maxTargetPH),
        stepValidator(this.targetPHStep),
      ]),
    ],
    [this.targetPH2BField.key]: [
      this.targetPH2BField.value,
      Validators.compose([
        Validators.min(this.minTargetPH),
        Validators.max(this.maxTargetPH),
        stepValidator(this.targetPHStep),
      ]),
    ],
    [this.pumpDuration2AField.key]: [
      this.pumpDuration2AField.value,
      Validators.compose([
        Validators.min(this.minPumpDuration),
        Validators.max(this.maxPumpDuration),
        stepValidator(this.pumpDurationStep),
      ]),
    ],
    [this.pumpDuration2BField.key]: [
      this.pumpDuration2BField.value,
      Validators.compose([
        Validators.min(this.minPumpDuration),
        Validators.max(this.maxPumpDuration),
        stepValidator(this.pumpDurationStep),
      ]),
    ],
    // pH + Chemostat controls
    [this.phChemoControlAField.key]: [this.phChemoControlAField.value],
    [this.phChemoControlBField.key]: [this.phChemoControlBField.value],
    [this.targetPHChemoAField.key]: [
      this.targetPHChemoAField.value,
      Validators.compose([
        Validators.min(this.minTargetPH),
        Validators.max(this.maxTargetPH),
        stepValidator(this.targetPHStep),
      ]),
    ],
    [this.targetPHChemoBField.key]: [
      this.targetPHChemoBField.value,
      Validators.compose([
        Validators.min(this.minTargetPH),
        Validators.max(this.maxTargetPH),
        stepValidator(this.targetPHStep),
      ]),
    ],
    [this.pumpDurationChemoAField.key]: [
      this.pumpDurationChemoAField.value,
      Validators.compose([
        Validators.min(this.minPumpDuration),
        Validators.max(this.maxPumpDuration),
        stepValidator(this.pumpDurationStep),
      ]),
    ],
    [this.pumpDurationChemoBField.key]: [
      this.pumpDurationChemoBField.value,
      Validators.compose([
        Validators.min(this.minPumpDuration),
        Validators.max(this.maxPumpDuration),
        stepValidator(this.pumpDurationStep),
      ]),
    ],
    [this.inputTypeChemoAField.key]: [this.inputTypeChemoAField.value],
    [this.inputTypeChemoBField.key]: [this.inputTypeChemoBField.value],
    [this.flowRatePHAField.key]: [
      this.flowRatePHAField.value,
      Validators.compose([
        Validators.min(this.minFlowRate),
        Validators.max(this.maxFlowRate),
        stepValidator(this.flowRateStep),
      ]),
    ],
    [this.flowRatePHBField.key]: [
      this.flowRatePHBField.value,
      Validators.compose([
        Validators.min(this.minFlowRate),
        Validators.max(this.maxFlowRate),
        stepValidator(this.flowRateStep),
      ]),
    ],
    // Fluoro controls
    [this.led1OnField.key]: [this.led1OnField.value],
    [this.led2OnField.key]: [this.led2OnField.value],
    [this.led3OnField.key]: [this.led3OnField.value],
    [this.led1IntensityField.key]: [
      this.led1IntensityField.value,
      Validators.compose([
        Validators.min(this.minLEDIntensity),
        Validators.max(this.maxLEDIntensity),
        stepValidator(this.ledIntensityStep),
      ]),
    ],
    [this.led2IntensityField.key]: [
      this.led2IntensityField.value,
      Validators.compose([
        Validators.min(this.minLEDIntensity),
        Validators.max(this.maxLEDIntensity),
        stepValidator(this.ledIntensityStep),
      ]),
    ],
    [this.led3IntensityField.key]: [
      this.led3IntensityField.value,
      Validators.compose([
        Validators.min(this.minLEDIntensity),
        Validators.max(this.maxLEDIntensity),
        stepValidator(this.ledIntensityStep),
      ]),
    ],
    [this.integrationTimeAField.key]: [
      this.integrationTimeAField.value,
      Validators.compose([
        Validators.min(this.minIntegrationTime),
        Validators.max(this.maxIntegrationTime),
        stepValidator(this.integrationTimeStep),
      ]),
    ],
    [this.integrationTimeBField.key]: [
      this.integrationTimeBField.value,
      Validators.compose([
        Validators.min(this.minIntegrationTime),
        Validators.max(this.maxIntegrationTime),
        stepValidator(this.integrationTimeStep),
      ]),
    ],
    [this.integrationTimeCField.key]: [
      this.integrationTimeCField.value,
      Validators.compose([
        Validators.min(this.minIntegrationTime),
        Validators.max(this.maxIntegrationTime),
        stepValidator(this.integrationTimeStep),
      ]),
    ],
    [this.integrationTimeDField.key]: [
      this.integrationTimeDField.value,
      Validators.compose([
        Validators.min(this.minIntegrationTime),
        Validators.max(this.maxIntegrationTime),
        stepValidator(this.integrationTimeStep),
      ]),
    ],
    [this.specGainAField.key]: [this.specGainAField.value],
    [this.specGainBField.key]: [this.specGainBField.value],
    [this.specGainCField.key]: [this.specGainCField.value],
    [this.specGainDField.key]: [this.specGainDField.value],
    [this.fluorescenceChannelsField.key]: [
      this.fluorescenceChannelsField.value,
    ],
    // Oxygen controls
    [this.salinityAField.key]: [
      this.salinityAField.value,
      Validators.compose([
        Validators.min(this.minSalinity),
        Validators.max(this.maxSalinity),
        stepValidator(this.salinityStep),
      ]),
    ],
    [this.salinityBField.key]: [
      this.salinityBField.value,
      Validators.compose([
        Validators.min(this.minSalinity),
        Validators.max(this.maxSalinity),
        stepValidator(this.salinityStep),
      ]),
    ],
    [this.salinityCField.key]: [
      this.salinityCField.value,
      Validators.compose([
        Validators.min(this.minSalinity),
        Validators.max(this.maxSalinity),
        stepValidator(this.salinityStep),
      ]),
    ],
    [this.salinityDField.key]: [
      this.salinityDField.value,
      Validators.compose([
        Validators.min(this.minSalinity),
        Validators.max(this.maxSalinity),
        stepValidator(this.salinityStep),
      ]),
    ],
    [this.spargingControlTypeAField.key]: [
      this.spargingControlTypeAField.value,
      [],
    ],
    [this.spargingControlTypeBField.key]: [
      this.spargingControlTypeBField.value,
      [],
    ],
    [this.spargingControlTypeCField.key]: [
      this.spargingControlTypeCField.value,
      [],
    ],
    [this.spargingControlTypeDField.key]: [
      this.spargingControlTypeDField.value,
      [],
    ],
    [this.noteField.key]: [
      '',
      [
        Validators.minLength(this.notesMinLength),
        Validators.maxLength(this.notesMaxLength),
      ],
    ],
    [this.experimentModeField.key]: [ExperimentMode.BatchCulture, []],
    // Notifications
    [this.enableNotificationsField.key]: [
      this.enableNotificationsField.value,
      [Validators.required],
    ],
    [this.notificationEmailField.key]: [this.notificationEmailField.value, []],
  });

  experimentTemplateForm = this.formBuilder.group({
    [this.experimentTemplatesField.key]: [null],
  });

  ngOnInit(): void {
    this.authenticationService.currentUser$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((user: User) => {
        this.currentUser.set(user);
      });

    this.laboratory.set(this.authenticationService.selectedLaboratory);

    this.unsavedCachedData.set(
      this.experimentService.retrieveAndClearUnsavedCache as
        | CreateExperiment
        | Experiment,
    );

    this.route.params.pipe(takeUntil(this.unsubscribe$)).subscribe((params: Params) => {
      this.experimentId.set(parseInt(params.id as string, 10));
    });

    const stateId = window.history.state?.templateId;
    if (
      typeof stateId === 'number' &&
      Number.isInteger(stateId) &&
      stateId > 0
    ) {
      this.templateId.set(stateId);
    }

    this.getFluorescenceChannels();

    if (this.laboratory()) {
      this.getExperimentTemplates();
      this.getLabMembers();
    }

    if (this.experimentId() !== 0 && this.experimentId() !== null) {
      if (this.unsavedCachedData()) {
        // Set form with cached draft data
        this.setDraftCachedData();
      } else {
        // Fetch experiment data from API
        this.getExperimentFromApi(this.experimentId());
      }
    } else if (this.unsavedCachedData()) {
      // Set form with cached new experiment data
      this.setUnsavedCachedData();
    } else {
      this.initialiseSubscriptions();
      this.initialiseCheckboxes();
      // Disable fluorescence fields initially since useFluorescence defaults to false
      this.setFluorescenceFieldsState(false);
      const emailControl = this.experimentControlsForm.get(
        this.notificationEmailField.key,
      );
      if (!emailControl) return;
      emailControl.disable();
    }
  }

  getExperimentTemplate(templateId: number): void {
    this.error.set(null);
    this.experimentTemplateService
      .getExperimentTemplateById(templateId)
      .subscribe({
        next: (response) => {
          this.newExperiment.set(response);
          if (this.hasEditAccess()) {
            this.initialiseSubscriptions();
          }
          // Handle fluoro selection
          if (response.useFluorescence) {
            this.selectedFluorescenceChannels.set(
              response.fluorescenceChannels,
            );
            this.filterFluorescenceChannelOptions();
          }

          this.nextButtonDisabled.set(false);
          this.useFluorescence.set(response.useFluorescence);
          this.useOxygen.set(response.useOxygen);
          this.usePH.set(response.usePH);
          this.useSparging.set(response.useSparging);
          // Enable/disable fluorescence fields based on loaded experiment data
          this.setFluorescenceFieldsState(this.useFluorescence());
          this.initialiseCheckboxes();
          this.updateFormValues(response, false);
        },
        error: (error: ErrorObject) => {
          this.error.set(error);
          this.openModal('error');
        },
      });
  }

  getExperimentTemplates(): void {
    const labId = this.laboratory()?.laboratoryId;
    if (labId == null) {
      this.fetchTemplatesError.set({ status: -1, message: 'Error fetching templates.', code: null });
      return;
    }

    this.fetchTemplatesError.set(null);
    this.cancelTemplates$.next();
    this.fetchTemplatesLoading.set(true);

    this.experimentTemplateService
      .getExperimentTemplatesForDropdown(labId)
      .pipe(takeUntil(this.cancelTemplates$), takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response) => {
          this.fetchTemplatesLoading.set(false);
          this.experimentTemplates.set(response);
          this.experimentTemplatesField.options =
            this.experimentTemplates().map((template) => ({
              key: template.id,
              value: template.templateName,
            }));

          if (this.templateId()) {
            const templateExists = this.experimentTemplates().some(
              (template) => template.id === this.templateId(),
            );
            if (templateExists) {
              this.experimentTemplateForm
                .get(this.experimentTemplatesField.key)
                .setValue(this.templateId());
              this.getExperimentTemplate(this.templateId());
            }
          }
        },
        error: (error: ErrorObject) => {
          this.fetchTemplatesLoading.set(false);
          this.fetchTemplatesError.set(error);
        },
      });
  }

  setDraftCachedData(): void {
    this.createdExperiment.set(this.unsavedCachedData() as Experiment);
    if (
      this.createdExperiment().experimentStatusId !==
        ExperimentStatus.DraftLocked &&
      this.hasEditAccess()
    ) {
      this.initialiseSubscriptions();
    }
    this.draftIsLocked.set(
      this.createdExperiment().experimentStatusId ===
        ExperimentStatus.DraftLocked,
    );
    this.getExperimentChangelog();
    this.nextButtonDisabled.set(false);
    this.useFluorescence.set(this.unsavedCachedData().useFluorescence);
    this.useOxygen.set(this.unsavedCachedData().useOxygen);
    this.usePH.set(this.unsavedCachedData().usePH);
    this.useSparging.set(this.unsavedCachedData().useSparging);
    if (this.useFluorescence()) {
      this.selectedFluorescenceChannels.set(
        this.createdExperiment().fluorescenceChannels,
      );
    }
    // Enable/disable fluorescence fields based on loaded experiment data
    this.setFluorescenceFieldsState(this.useFluorescence());
    this.initialiseCheckboxes();
    this.filterFluorescenceChannelOptions();
    this.updateFormValues(this.unsavedCachedData());
  }

  setUnsavedCachedData(): void {
    this.newExperiment.set(this.unsavedCachedData() as CreateExperiment);
    this.initialiseSubscriptions();
    this.useFluorescence.set(this.unsavedCachedData().useFluorescence);
    this.useOxygen.set(this.unsavedCachedData().useOxygen);
    this.usePH.set(this.unsavedCachedData().usePH);
    this.useSparging.set(this.unsavedCachedData().useSparging);
    // Enable/disable fluorescence fields based on loaded experiment data
    this.setFluorescenceFieldsState(this.useFluorescence());
    this.initialiseCheckboxes();
    if (this.useFluorescence()) {
      this.selectedFluorescenceChannels.set(
        this.fluorescenceChannels().filter((channel) =>
          this.newExperiment().fluorescenceChannels?.some(
            (fc: number | FluorescenceChannel) =>
              (typeof fc === 'number' ? fc : fc.memorySlot) ===
              channel.memorySlot,
          ),
        ),
      );
    }
    this.filterFluorescenceChannelOptions();
    this.updateFormValues(this.unsavedCachedData(), false);
  }

  initialiseSubscriptions(): void {
    if (this.subscriptionsInitialised) {
      return;
    }
    this.subscriptionsInitialised = true;
    this.experimentTemplateForm
      .get(this.experimentTemplatesField.key)
      .valueChanges.pipe(takeUntil(this.unsubscribe$))
      .subscribe((templateId) => {
        const selectedTemplate = this.experimentTemplates().find(
          (template) => template.id === templateId,
        );
        if (selectedTemplate) {
          this.getExperimentTemplate(templateId);
        }
      });
    this.experimentControlsForm
      .get(this.enableNotificationsField.key)
      ?.valueChanges.pipe(takeUntil(this.unsubscribe$))
      .subscribe((enabled) => {
        const emailControl = this.experimentControlsForm.get(
          this.notificationEmailField.key,
        );
        if (!emailControl) return;
        emailControl.setValidators(enabled ? [Validators.required] : []);
        if (enabled) {
          emailControl.enable();
          const notificationEmail = this.createdExperiment()?.notificationEmail;
          const currentUserEmail = this.currentUser()?.email || '';
          emailControl.setValue(notificationEmail || currentUserEmail);
        } else {
          emailControl.setValue('');
          emailControl.disable();
        }
        emailControl.updateValueAndValidity();
      });
    this.subscribeNotificationEmail();
    this.experimentControlsForm
      .get(this.maintainODField.key)
      .valueChanges.pipe(takeUntil(this.unsubscribe$))
      .subscribe((value) => {
        const targetODFields = ['A', 'B', 'C', 'D'] as const;
        targetODFields.forEach((flask) => {
          if (value === false) {
            // When maintainOD is false, only enable target OD if the individual control is active
            const turbActiveField = this[`turb${flask}ActiveField` as const];
            const isControlActive = this.f[turbActiveField.key]
              .value as boolean;
            this.setFlaskControlState(flask, 'turbidostat', isControlActive);
          } else {
            // When maintainOD is true, disable all target OD fields
            this.setFlaskControlState(flask, 'turbidostat', false);
          }
        });
      });

    // Stirring speed controls
    (['A', 'B', 'C', 'D'] as Flask[]).forEach((flask) => {
      const setSpeedField = this[`setStirringSpeed${flask}Field` as const];
      this.experimentControlsForm
        .get(setSpeedField.key)
        .valueChanges.pipe(takeUntil(this.unsubscribe$))
        .subscribe((value) => {
          this.updateStirringSpeedState(flask, value);
        });
    });

    // Temperature controls
    (['A', 'B', 'C', 'D'] as const).forEach((flask) => {
      const tempControlField = this[`temperatureControl${flask}Field` as const];
      this.experimentControlsForm
        .get(tempControlField.key)
        .valueChanges.pipe(takeUntil(this.unsubscribe$))
        .subscribe((value) => {
          this.setFlaskControlState(flask, 'temperature', value);
        });
    });

    // Turbidostat controls
    (['A', 'B', 'C', 'D'] as const).forEach((flask) => {
      const turbActiveField = this[`turb${flask}ActiveField` as const];
      this.experimentControlsForm
        .get(turbActiveField.key)
        .valueChanges.pipe(takeUntil(this.unsubscribe$))
        .subscribe((value) => {
          // Only enable target OD fields if maintainOD is false
          const maintainOD = this.f[this.maintainODField.key].value as boolean;
          const shouldEnable = value && !maintainOD;
          this.setFlaskControlState(flask, 'turbidostat', shouldEnable);
        });
    });

    // Chemostat controls
    (['A', 'B', 'C', 'D'] as const).forEach((flask) => {
      const chemoActiveField = this[`chemo${flask}ActiveField` as const];
      this.experimentControlsForm
        .get(chemoActiveField.key)
        .valueChanges.pipe(takeUntil(this.unsubscribe$))
        .subscribe((value) => {
          this.setFlaskControlState(flask, 'chemostat', value);
        });
    });

    // pH controls
    (['A', 'B', 'C', 'D'] as const).forEach((flask) => {
      const phControlField = this[`phControl${flask}Field` as const];
      this.experimentControlsForm
        .get(phControlField.key)
        .valueChanges.pipe(takeUntil(this.unsubscribe$))
        .subscribe((value) => {
          this.setFlaskControlState(flask, 'ph', value);
        });
    });

    // pH 2x controls
    (['A', 'B'] as const).forEach((flask) => {
      const ph2ControlField = this[`ph2Control${flask}Field` as const];
      this.experimentControlsForm
        .get(ph2ControlField.key)
        .valueChanges.pipe(takeUntil(this.unsubscribe$))
        .subscribe((value) => {
          this.setFlaskControlState(flask, 'ph2', value);
        });
    });

    // pH Chemo controls
    (['A', 'B'] as const).forEach((flask) => {
      const phChemoControlField = this[`phChemoControl${flask}Field` as const];
      this.experimentControlsForm
        .get(phChemoControlField.key)
        .valueChanges.pipe(takeUntil(this.unsubscribe$))
        .subscribe((value) => {
          this.setFlaskControlState(flask, 'phChemo', value);
        });
    });

    // Morbidostat controls
    (['A', 'B', 'C', 'D'] as const).forEach((flask) => {
      const morbidostatActiveField = this[`morbidostat${flask}ActiveField` as const];
      this.experimentControlsForm
        .get(morbidostatActiveField.key)!
        .valueChanges.pipe(takeUntil(this.unsubscribe$))
        .subscribe((value) => {
          this.setFlaskControlState(flask, 'morbidostat', value);
        });
    });

    // Fluoro channels
    this.experimentControlsForm
      .get(this.fluorescenceChannelsField.key)
      .valueChanges.pipe(takeUntil(this.unsubscribe$))
      .subscribe((value) => {
        const channel = this.fluorescenceChannels().find(
          (option) => option.memorySlot === value,
        );
        if (channel && this.selectedFluorescenceChannels().length < 4) {
          this.selectedFluorescenceChannels.update((channels) => [
            ...channels,
            channel,
          ]);
          this.fluorescenceChannelsField.options =
            this.fluorescenceChannelsField.options.filter(
              (option) => option.key !== value,
            );
          if (this.selectedFluorescenceChannels().length === 4) {
            this.experimentControlsForm
              .get(this.fluorescenceChannelsField.key)
              ?.disable();
          }
        }
      });
  }

  getExperimentFromApi(experimentId: number): void {
    this.getExperimentError.set(null);
    this.experimentService.getExperimentById(experimentId).subscribe({
      next: (response: Experiment) => {
        this.createdExperiment.set(response);
        if (
          this.createdExperiment().experimentStatusId !==
            ExperimentStatus.DraftLocked &&
          this.hasEditAccess()
        ) {
          this.initialiseSubscriptions();
        }
        this.draftIsLocked.set(
          this.createdExperiment().experimentStatusId ===
            ExperimentStatus.DraftLocked,
        );
        // Handle fluoro selection
        if (response.useFluorescence) {
          this.selectedFluorescenceChannels.set(response.fluorescenceChannels);
          this.filterFluorescenceChannelOptions();
        }

        this.getExperimentChangelog();

        this.nextButtonDisabled.set(false);
        this.useFluorescence.set(response.useFluorescence);
        this.useOxygen.set(response.useOxygen);
        this.usePH.set(response.usePH);
        this.useSparging.set(response.useSparging);
        // Enable/disable fluorescence fields based on loaded experiment data
        this.setFluorescenceFieldsState(this.useFluorescence());
        this.initialiseCheckboxes();
        this.updateFormValues(response);
      },
      error: (error: ErrorObject) => {
        this.getExperimentError.set(error);
      },
    });
  }

  updateFormValues(
    experimentData: CreateExperiment | Experiment | ExperimentTemplate,
    isDraftExperiment: boolean = true,
  ): void {
    // Combine all form values using helper methods
    const formValues = {
      ...this.getBasicFormValues(experimentData),
      ...this.getDefaultModuleFormValues(experimentData),
      ...this.getTurbidostatFormValues(experimentData),
      ...this.getChemostatFormValues(experimentData),
      ...this.getMorbidostatFormValues(experimentData),
      ...this.getPHControlFormValues(experimentData),
      ...this.getFluorescenceFormValues(experimentData),
      ...this.getOxygenFormValues(experimentData),
      ...this.getSpargingFormValues(experimentData),
    };

    // Unsaved experiment - remove selected fluorescence channels from available options
    if (!isDraftExperiment && experimentData.useFluorescence) {
      this.selectedFluorescenceChannels().forEach((channel) => {
        this.fluorescenceChannelsField.options =
          this.fluorescenceChannelsField.options.filter(
            (option) => option.key !== channel.memorySlot,
          );
      });
    }
    this.experimentControlsForm.patchValue(formValues);

    // patchValue fires enableNotifications.valueChanges, which pre-fills
    // notificationEmail with the current user's email when the stored value is
    // null or empty string. Only override that pre-fill if there is an explicit email to restore
    // (e.g. a saved draft, template, or existing experiment); otherwise the subscription's value is correct.
    const emailControl = this.experimentControlsForm.get(this.notificationEmailField.key);
    if (emailControl) {
      if (experimentData.notificationEmail) {
        emailControl.setValue(experimentData.notificationEmail, { emitEvent: false });
      }
      experimentData.enableNotifications
        ? emailControl.enable({ emitEvent: false })
        : emailControl.disable({ emitEvent: false });
      this.notificationEmailValue.set((emailControl.value as string) ?? '');
    }
    this.setModuleCheckboxes(isDraftExperiment);

    if (isDraftExperiment) {
      this.experimentControlsForm.markAsPristine();
    } else {
      this.experimentControlsForm.markAsDirty();
    }

    if (
      this.draftIsLocked() ||
      (!this.hasEditAccess() &&
        this.createdExperiment()?.experimentStatusId === ExperimentStatus.Draft)
    ) {
      this.experimentControlsForm.disable();
      this.moduleSelectForm.disable();
    }
  }

  setModuleCheckboxes(isDraftExperiment: boolean): void {
    const oxygenControlIndex = this.moduleCheckboxesField.options.findIndex(
      (option) => option.key === 'Oxygen',
    );

    if (oxygenControlIndex > -1) {
      this.checkboxesFormArray.controls[oxygenControlIndex].setValue(
        isDraftExperiment
          ? this.createdExperiment().useOxygen
          : this.newExperiment().useOxygen,
      );
    }

    const pHControlIndex = this.moduleCheckboxesField.options.findIndex(
      (option) => option.key === 'pH',
    );

    if (pHControlIndex > -1) {
      this.checkboxesFormArray.controls[pHControlIndex].setValue(
        isDraftExperiment
          ? this.createdExperiment().usePH
          : this.newExperiment().usePH,
      );
    }

    const fluorescenceControlIndex =
      this.moduleCheckboxesField.options.findIndex(
        (option) => option.key === 'Fluorescence',
      );
    if (fluorescenceControlIndex > -1) {
      this.checkboxesFormArray.controls[fluorescenceControlIndex].setValue(
        isDraftExperiment
          ? this.createdExperiment().useFluorescence
          : this.newExperiment().useFluorescence,
      );
    }

    const spargingControlIndex = this.moduleCheckboxesField.options.findIndex(
      (option) => option.key === 'Sparging',
    );
    if (spargingControlIndex > -1) {
      this.checkboxesFormArray.controls[spargingControlIndex].setValue(
        isDraftExperiment
          ? this.createdExperiment().useSparging
          : this.newExperiment().useSparging,
      );
    }

    if (isDraftExperiment) {
      this.moduleSelectForm.markAsPristine();
    } else {
      this.moduleSelectForm.markAsDirty();
    }
    if (this.draftIsLocked()) {
      this.moduleSelectForm.disable();
    }
  }

  modalButtonClicked(buttonId: string): void {
    switch (buttonId) {
      case 'next-button':
        void this.router.navigate([
          `experiment/${this.experimentId()}/select-device`,
        ]);
        break;
      case 'close-create-button':
        this.closeModal('create-experiment');
        break;
      case 'close-error-button':
        this.closeModal('error');
        break;
      case 'close-update-button':
        this.closeModal('update-experiment');
        break;
      case 'close-device-warning-button':
        this.closeModal('device-warning');
        break;
      case 'cancel-button':
        this.allowNavigation.next(false);
        this.closeModal('attention');
        break;
      case 'confirm-navigation':
        this.allowNavigation.next(true);
        this.closeModal('attention');
        break;
      case 'delete-success-button':
        this.closeModal('delete-success');
        this.allowNavigation.next(true);
        void this.router.navigate(['dashboard']);
        break;
      case 'error-primary-button':
        this.allowNavigation.next(false);
        this.deleteError.set(null);
        this.closeModal('delete-error');
        break;
    }
  }

  handleSubmit(): void {
    if (this.experimentId()) {
      this.updateExperiment();
    } else {
      this.createExperiment();
    }
  }

  createExperiment(): void {
    // If form is already being submitted, exit
    if (this.isProcessing()) {
      return;
    }

    this.isProcessing.set(true);
    this.error.set(null);

    const formData = this.getCreateExperimentFormValues();
    this.newExperiment.set(formData);

    this.experimentService.createExperiment(this.newExperiment()).subscribe({
      next: (response: Experiment) => {
        this.isProcessing.set(false);
        this.nextButtonDisabled.set(false);
        this.createdExperiment.set(response);
        this.draftIsLocked.set(false);
        this.experimentId.set(response.id);
        this.updateIdInUrl(response.id);
        this.getExperimentChangelog();
        this.updateFormValues(this.createdExperiment());
        this.experimentControlsForm.markAsPristine(); // TODO this should be handled by the form service
        this.openModal('create-experiment');
      },
      error: (error: ErrorObject) => {
        this.error.set(error);
        this.isProcessing.set(false);
        this.openModal('error');
      },
    });
  }

  updateExperiment(): void {
    // If form is already being submitted, exit
    if (this.isProcessing()) {
      return;
    }

    this.isProcessing.set(true);
    this.error.set(null);

    // Extract form values using helper methods
    const turbidostatValues = this.getTurbidostatValues();
    const chemostatValues = this.getChemostatValues();
    const morbidostatValues = this.getMorbidostatValues();
    const phControlValues = this.getPHControlValues();
    const fluorescenceValues = this.getFluorescenceValues();
    const oxygenValues = this.getOxygenValues();
    const spargingValues = this.getSpargingValues();

    const updateExperiment: ExperimentUpdate = {
      id: this.experimentId(),
      experimentStatusId: ExperimentStatus.Draft,
      useFluorescence: this.useFluorescence(),
      useOxygen: this.useOxygen(),
      usePH: this.usePH(),
      useSparging: this.useSparging(),
      experimentMode: this.f[this.experimentModeField.key]
        .value as ExperimentMode,
      name: this.f[this.experimentNameField.key].value as string,
      organism: this.f[this.organismField.key].value as string,
      strain: this.f[this.strainField.key].value as string,
      media: this.f[this.mediaField.key].value as string,
      // Default module values
      ambientTemperature: this.f[this.ambientTemperatureField.key]
        .value as number,
      temperatureControlA: this.f[this.temperatureControlAField.key]
        .value as boolean,
      temperatureControlB: this.f[this.temperatureControlBField.key]
        .value as boolean,
      temperatureControlC: this.f[this.temperatureControlCField.key]
        .value as boolean,
      temperatureControlD: this.f[this.temperatureControlDField.key]
        .value as boolean,
      cultureTemperatureA: this.f[this.temperatureControlAField.key].value === true
        ? (this.f[this.temperatureAField.key].value as number)
        : undefined,
      cultureTemperatureB: this.f[this.temperatureControlBField.key].value === true
        ? (this.f[this.temperatureBField.key].value as number)
        : undefined,
      cultureTemperatureC: this.f[this.temperatureControlCField.key].value === true
        ? (this.f[this.temperatureCField.key].value as number)
        : undefined,
      cultureTemperatureD: this.f[this.temperatureControlDField.key].value === true
        ? (this.f[this.temperatureDField.key].value as number)
        : undefined,
      samplingInterval: this.f[this.samplingTimeField.key].value as number,
      duration: this.f[this.experimentTimeField.key].value as number,
      speedRPMA: this.f[this.speedAField.key].value as number,
      speedRPMB: this.f[this.speedBField.key].value as number,
      speedRPMC: this.f[this.speedCField.key].value as number,
      speedRPMD: this.f[this.speedDField.key].value as number,
      // Turbidostat controls
      ...turbidostatValues,
      // Chemostat controls
      ...chemostatValues,
      // Morbidostat controls
      ...morbidostatValues,
      // pH controls
      ...phControlValues,
      // LED controls, submitted even if fluorescence is not being measured
      led1On: fluorescenceValues.led1On,
      led2On: fluorescenceValues.led2On,
      led3On: fluorescenceValues.led3On,
      led1Intensity: fluorescenceValues.led1Intensity,
      led2Intensity: fluorescenceValues.led2Intensity,
      led3Intensity: fluorescenceValues.led3Intensity,
      // Fluorescence controls - only submitted when fluorescence module is selected
      integrationTimeA: fluorescenceValues.integrationTimeA,
      integrationTimeB: fluorescenceValues.integrationTimeB,
      integrationTimeC: fluorescenceValues.integrationTimeC,
      integrationTimeD: fluorescenceValues.integrationTimeD,
      specGainA: fluorescenceValues.specGainA,
      specGainB: fluorescenceValues.specGainB,
      specGainC: fluorescenceValues.specGainC,
      specGainD: fluorescenceValues.specGainD,
      fluorescenceChannels: fluorescenceValues.fluorescenceChannels,
      // Oxygen controls
      ...oxygenValues,
      // Sparging controls (airflow set in select-device)
      spargingControlTypeA: spargingValues.spargingControlTypeA,
      spargingControlTypeB: spargingValues.spargingControlTypeB,
      spargingControlTypeC: spargingValues.spargingControlTypeC,
      spargingControlTypeD: spargingValues.spargingControlTypeD,
      notes: this.f[this.noteField.key].value as string,
      enableNotifications: this.f[this.enableNotificationsField.key]
        .value as boolean,
      notificationEmail: this.f[this.enableNotificationsField.key].value
        ? (this.f[this.notificationEmailField.key].value as string)
        : null,
    };

    this.experimentService.updateDraftExperiment(updateExperiment).subscribe({
      next: (response) => {
        this.isProcessing.set(false);
        this.nextButtonDisabled.set(false);
        this.createdExperiment.set(response);
        this.draftIsLocked.set(
          this.createdExperiment().experimentStatusId ===
            ExperimentStatus.DraftLocked,
        );
        this.experimentId.set(response.id);
        this.getExperimentChangelog();
        this.updateFormValues(this.createdExperiment());
        this.openModal('update-experiment');
      },
      error: (error: ErrorObject) => {
        this.error.set(error);
        this.isProcessing.set(false);
        this.openModal('error');
      },
    });
  }

  deleteDraft(): void {
    // If request has already been submitted, exit
    if (this.isProcessing() || this.deleteIsProcessing()) {
      return;
    }

    this.deleteError.set(null);
    this.deleteIsProcessing.set(true);

    this.experimentService.deleteExperimentById(this.experimentId()).subscribe({
      next: () => {
        this.deleteIsProcessing.set(false);
        this.closeModal('delete-draft');
        this.openModal('delete-success');
      },
      error: (error: ErrorObject) => {
        this.deleteIsProcessing.set(false);
        this.deleteError.set(error);
        this.closeModal('delete-draft');
        this.openModal('delete-error');
      },
    });
  }

  getExperimentChangelog(): void {
    this.changelogError.set(null);
    this.experimentService
      .getExperimentChangelog(
        this.experimentId(),
        this.currentSort().sortBy,
        this.currentSort().sortDirection,
        this.pageSize(),
        this.pageRequested(),
      )
      .subscribe({
        // If successful, update the changelogList and pagination vars
        // If error, set error to be displayed
        next: (response: ChangelogResponse) => {
          this.changelogList.set(response.changelogList);
          if (response.paginationData) {
            this.currentPage.set(response.paginationData.CurrentPage);
            this.pageSize.set(response.paginationData.PageSize);
            this.totalPages.set(response.paginationData.TotalPages);
            this.hasNext.set(response.paginationData.HasNext);
            this.hasPrevious.set(response.paginationData.HasPrevious);
          }
        },
        error: (error: ErrorObject) => {
          this.changelogError.set(error);
        },
      });
  }

  checkRequiredModules(): void {
    const haveRequiredModules = allDevicesHaveRequiredModules(
      this.createdExperiment().devices,
      this.requiredDeviceModules(),
    );
    if (!haveRequiredModules) {
      this.openModal('device-warning');
    }
  }

  onNext(): void {
    // Route to the device setup page
    void this.router.navigate([
      `experiment/${this.experimentId()}/select-device`,
    ]);
  }

  private getCreateExperimentFormValues(): CreateExperiment {
    // Extract form values using helper methods
    const turbidostatValues = this.getTurbidostatValues();
    const chemostatValues = this.getChemostatValues();
    const morbidostatValues = this.getMorbidostatValues();
    const phControlValues = this.getPHControlValues();
    const fluorescenceValues = this.getFluorescenceValues();
    const oxygenValues = this.getOxygenValues();
    const spargingValues = this.getSpargingValues();

    const cache: CreateExperiment = {
      useFluorescence: this.useFluorescence(),
      useOxygen: this.useOxygen(),
      usePH: this.usePH(),
      useSparging: this.useSparging(),
      experimentMode: this.f[this.experimentModeField.key]
        .value as ExperimentMode,
      name: this.f[this.experimentNameField.key].value as string,
      organism: this.f[this.organismField.key].value as string,
      strain: this.f[this.strainField.key].value as string,
      media: this.f[this.mediaField.key].value as string,
      // Default module settings
      ambientTemperature: this.f[this.ambientTemperatureField.key]
        .value as number,
      samplingInterval: this.f[this.samplingTimeField.key].value as number,
      duration: this.f[this.experimentTimeField.key].value as number,
      speedRPMA: this.f[this.speedAField.key].value as number,
      speedRPMB: this.f[this.speedBField.key].value as number,
      speedRPMC: this.f[this.speedCField.key].value as number,
      speedRPMD: this.f[this.speedDField.key].value as number,
      temperatureControlA: this.f[this.temperatureControlAField.key]
        .value as boolean,
      temperatureControlB: this.f[this.temperatureControlBField.key]
        .value as boolean,
      temperatureControlC: this.f[this.temperatureControlCField.key]
        .value as boolean,
      temperatureControlD: this.f[this.temperatureControlDField.key]
        .value as boolean,
      cultureTemperatureA: this.f[this.temperatureControlAField.key].value === true
        ? (this.f[this.temperatureAField.key].value as number)
        : undefined,
      cultureTemperatureB: this.f[this.temperatureControlBField.key].value === true
        ? (this.f[this.temperatureBField.key].value as number)
        : undefined,
      cultureTemperatureC: this.f[this.temperatureControlCField.key].value === true
        ? (this.f[this.temperatureCField.key].value as number)
        : undefined,
      cultureTemperatureD: this.f[this.temperatureControlDField.key].value === true
        ? (this.f[this.temperatureDField.key].value as number)
        : undefined,
      // Turbidostat values
      ...turbidostatValues,
      // Chemostat values
      ...chemostatValues,
      // Morbidostat values
      ...morbidostatValues,
      // pH controls
      ...phControlValues,
      // Fluorescence controls
      led1On: fluorescenceValues.led1On,
      led2On: fluorescenceValues.led2On,
      led3On: fluorescenceValues.led3On,
      led1Intensity: fluorescenceValues.led1Intensity,
      led2Intensity: fluorescenceValues.led2Intensity,
      led3Intensity: fluorescenceValues.led3Intensity,
      integrationTimeA: fluorescenceValues.integrationTimeA,
      integrationTimeB: fluorescenceValues.integrationTimeB,
      integrationTimeC: fluorescenceValues.integrationTimeC,
      integrationTimeD: fluorescenceValues.integrationTimeD,
      specGainA: fluorescenceValues.specGainA,
      specGainB: fluorescenceValues.specGainB,
      specGainC: fluorescenceValues.specGainC,
      specGainD: fluorescenceValues.specGainD,
      fluorescenceChannels: fluorescenceValues.fluorescenceChannels,
      // Oxygen controls
      ...oxygenValues,
      // Sparging controls
      spargingControlTypeA: spargingValues.spargingControlTypeA,
      spargingControlTypeB: spargingValues.spargingControlTypeB,
      spargingControlTypeC: spargingValues.spargingControlTypeC,
      spargingControlTypeD: spargingValues.spargingControlTypeD,
      notes: this.f[this.noteField.key].value as string,
      enableNotifications: this.f[this.enableNotificationsField.key]
        .value as boolean,
      notificationEmail: this.f[this.enableNotificationsField.key].value
        ? (this.f[this.notificationEmailField.key].value as string)
        : null,
    };

    return cache;
  }

  private getDraftExperimentFormValues(): Experiment {
    // Extract form values using helper methods
    const turbidostatValues = this.getTurbidostatValues();
    const chemostatValues = this.getChemostatValues();
    const morbidostatValues = this.getMorbidostatValues();
    const phControlValues = this.getPHControlValues();
    const fluorescenceValues = this.getFluorescenceValues();
    const oxygenValues = this.getOxygenValues();
    const spargingValues = this.getSpargingValues();

    const cache: Experiment = {
      id: this.experimentId(),
      experimentStatusId: ExperimentStatus.Draft,
      name: this.f[this.experimentNameField.key].value as string,
      organism: this.f[this.organismField.key].value as string,
      strain: this.f[this.strainField.key].value as string,
      media: this.f[this.mediaField.key].value as string,
      // Default module values
      ambientTemperature: this.f[this.ambientTemperatureField.key]
        .value as number,
      samplingInterval: this.f[this.samplingTimeField.key].value as number,
      duration: this.f[this.experimentTimeField.key].value as number,
      speedRPMA: this.f[this.speedAField.key].value as number,
      speedRPMB: this.f[this.speedBField.key].value as number,
      speedRPMC: this.f[this.speedCField.key].value as number,
      speedRPMD: this.f[this.speedDField.key].value as number,
      temperatureControlA: this.f[this.temperatureControlAField.key]
        .value as boolean,
      temperatureControlB: this.f[this.temperatureControlBField.key]
        .value as boolean,
      temperatureControlC: this.f[this.temperatureControlCField.key]
        .value as boolean,
      temperatureControlD: this.f[this.temperatureControlDField.key]
        .value as boolean,
      cultureTemperatureA: this.f[this.temperatureAField.key].value as number,
      cultureTemperatureB: this.f[this.temperatureBField.key].value as number,
      cultureTemperatureC: this.f[this.temperatureCField.key].value as number,
      cultureTemperatureD: this.f[this.temperatureDField.key].value as number,
      useFluorescence: this.useFluorescence(),
      useOxygen: this.useOxygen(),
      usePH: this.usePH(),
      useSparging: this.useSparging(),
      experimentMode: this.f[this.experimentModeField.key]
        .value as ExperimentMode,
      // Turbidostat controls
      ...turbidostatValues,
      // Chemostat controls
      ...chemostatValues,
      // Morbidostat controls
      ...morbidostatValues,
      // pH controls
      ...phControlValues,
      // LED controls, submitted even if fluorescence is not being measured
      led1On: fluorescenceValues.led1On,
      led2On: fluorescenceValues.led2On,
      led3On: fluorescenceValues.led3On,
      led1Intensity: fluorescenceValues.led1Intensity,
      led2Intensity: fluorescenceValues.led2Intensity,
      led3Intensity: fluorescenceValues.led3Intensity,
      // Fluorescence controls - only submitted when fluorescence module is selected
      integrationTimeA: fluorescenceValues.integrationTimeA,
      integrationTimeB: fluorescenceValues.integrationTimeB,
      integrationTimeC: fluorescenceValues.integrationTimeC,
      integrationTimeD: fluorescenceValues.integrationTimeD,
      specGainA: fluorescenceValues.specGainA,
      specGainB: fluorescenceValues.specGainB,
      specGainC: fluorescenceValues.specGainC,
      specGainD: fluorescenceValues.specGainD,
      fluorescenceChannels: this.selectedFluorescenceChannels(),
      // Oxygen controls
      ...oxygenValues,
      // Sparging controls (airflow set in select-device)
      spargingControlTypeA: spargingValues.spargingControlTypeA,
      spargingControlTypeB: spargingValues.spargingControlTypeB,
      spargingControlTypeC: spargingValues.spargingControlTypeC,
      spargingControlTypeD: spargingValues.spargingControlTypeD,
    };
    return cache;
  }

  canExit(): Promise<boolean> | boolean {
    return this.isNavigationAllowed();
  }

  ngOnDestroy(): void {
    if (
      this.authEventService.isUnauthorizedNavigationPending &&
      !this.experimentControlsForm.pristine
    ) {
      try {
        let formValues: CreateExperiment | Experiment | ExperimentTemplate;
        if (this.experimentId() !== 0) {
          formValues = this.getDraftExperimentFormValues();
        } else {
          formValues = this.getCreateExperimentFormValues();
        }
        this.experimentService.cacheUnsavedParameters(formValues);
      } catch (error) {
        console.error(
          'Failed to cache unsaved experiment parameters on destroy',
          error,
        );
      }
    }

    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
